import { useEffect, useRef } from 'react';
import { selectParagraph } from '../state/selectors';
import { Banner } from './Banner';
import { useEnv, useFormState, useMoney } from './context';
import { fieldId } from './format';
import { ArrowRight } from './icons';
import { ArrowLeft } from './icons';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3, SubmitButton } from './Step3';
import { Stepper } from './Stepper';
import { Success } from './Success';
import { scrollToFormTop } from './scroll';
import { startStickyFallback } from './sticky';
import { Summary, SummaryBody } from './Summary';

const STICKY_GAP = 0; // matches .cbf-aside { top: var(--wp-admin--admin-bar--height, 0px) }
const DESKTOP_MIN_WIDTH = 960;

export function App() {
  const { controller } = useEnv();
  const money = useMoney();
  const state = useFormState();
  const rootRef = useRef<HTMLDivElement>(null);
  const layoutRef = useRef<HTMLDivElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const ready = state.status === 'ready' || state.status === 'submitting';

  // Native sticky is declared in CSS; this emulates it where the host page's overflow rules break it.
  useEffect(() => {
    if (!ready || !layoutRef.current || !cellRef.current || !panelRef.current) return;
    return startStickyFallback({ cell: cellRef.current, panel: panelRef.current, layout: layoutRef.current, gap: STICKY_GAP, minWidth: DESKTOP_MIN_WIDTH });
  }, [ready]);

  // Step changes scroll back to the top of the form. Skips the first render (the page should not
  // jump to the form on load) and defers to the focus effect below when a field needs attention.
  const lastView = useRef<string | null>(null);
  useEffect(() => {
    const view = state.status === 'succeeded' ? 'done' : `step-${state.step}`;
    const previous = lastView.current;
    lastView.current = view;
    if (previous === null || previous === view || state.focusKey) return;
    scrollToFormTop(rootRef.current);
  }, [state.step, state.status, state.focusKey]);

  // Focus effect: the controller names the field; we find it after the pane has rendered.
  useEffect(() => {
    if (!state.focusKey) return;
    const key = state.focusKey;
    const el =
      document.getElementById(fieldId(key)) ??
      document.querySelector<HTMLElement>(`[data-cbf-field="${key}"] input, [data-cbf-field="${key}"] select, [data-cbf-field="${key}"] textarea, [data-cbf-field="${key}"] button, [data-cbf-field="${key}"]`);
    if (el) {
      el.focus({ preventScroll: true });
      el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }
    controller.send({ type: 'FOCUSED' });
  }, [state.focusKey, state.step, controller]);

  if (state.status === 'booting') {
    return (
      <div className="cbf-root">
        <p className="cbf-status" role="status">
          Loading the booking form…
        </p>
      </div>
    );
  }
  if (state.status === 'failed' || !state.bootstrap) {
    return (
      <div className="cbf-root">
        <div className="cbf-card" role="alert">
          <h2 className="cbf-h2">Booking is unavailable right now</h2>
          <p className="cbf-p">{state.bootError ?? 'Please refresh the page or try again in a few minutes.'}</p>
        </div>
      </div>
    );
  }

  const settings = state.bootstrap.settings;
  const title = selectParagraph(state, 'complete_your_booking') ?? `Book with ${settings.name ?? 'us'}`;
  const lead = selectParagraph(state, 'few_details');
  const errors = controller.describeErrors();
  const total = state.estimate.data?.total;

  if (state.status === 'succeeded') {
    return (
      <div className="cbf-root">
        <div className="cbf-header">
          <h1 className="cbf-title">{title}</h1>
        </div>
        <Success />
      </div>
    );
  }

  return (
    <div className="cbf-root" ref={rootRef}>
      <header className="cbf-header">
        <h1 className="cbf-title">{title}</h1>
        {lead ? <p className="cbf-lead">{lead}</p> : null}
        <Stepper step={state.step} />
      </header>
      <div className="cbf-layout" ref={layoutRef}>
        <div className="cbf-form">
          <Banner messages={state.banner} />
          <div key={state.step} role="group" aria-label={`Step ${state.step} of 3: ${state.step === 1 ? 'your details and date' : state.step === 2 ? 'your vehicle' : 'payment'}`} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {state.step === 1 ? <Step1 errors={errors.step1} /> : state.step === 2 ? <Step2 errors={errors.step2} /> : <Step3 errors={errors.step3} />}
          </div>
          <div className="cbf-footer cbf-footer-desktop">
            {state.step === 1 ? (
              <span className="cbf-footer-note">
                <span className="cbf-req">*</span> Required
              </span>
            ) : (
              <button type="button" className="cbf-btn cbf-btn-secondary" onClick={() => controller.back()} disabled={state.status === 'submitting'}>
                <ArrowLeft />
                <span>Back</span>
              </button>
            )}
            {state.step === 3 ? (
              <SubmitButton />
            ) : (
              <button type="button" className="cbf-btn" onClick={() => void controller.next()} disabled={state.step === 1 && state.location.status === 'checking'}>
                <span>{state.step === 1 ? 'Next: your vehicle' : 'Next: payment'}</span>
                <ArrowRight />
              </button>
            )}
          </div>
        </div>
        <div className="cbf-aside" ref={cellRef}>
          <div className="cbf-aside-inner" ref={panelRef}>
            <Summary />
          </div>
        </div>
      </div>

      <p className="cbf-sr" aria-live="polite">
        {typeof total === 'number' ? `Total updated: ${money(total)}` : ''}
      </p>

      <div className="cbf-bar">
        {state.step === 1 ? (
          <button type="button" className="cbf-btn cbf-btn-block" onClick={() => void controller.next()} disabled={state.location.status === 'checking'}>
            <span>Next: your vehicle</span>
            <ArrowRight />
          </button>
        ) : (
          <>
            <details className="cbf-bar-total-wrap">
              <summary className="cbf-bar-total" style={{ listStyle: 'none', cursor: 'pointer' }}>
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="cbf-sum-total-label" style={{ fontSize: 15 }}>
                    Total
                  </span>
                </span>
                <span className="cbf-sum-total cbf-mono">{typeof total === 'number' ? money(total) : '—'}</span>
              </summary>
              <div className="cbf-summary" style={{ marginTop: 8 }}>
                <SummaryBody compact />
              </div>
            </details>
            {state.step === 3 ? <SubmitButton block /> : (
              <button type="button" className="cbf-btn cbf-btn-block" onClick={() => void controller.next()}>
                <span>Next: payment</span>
                <ArrowRight />
              </button>
            )}
            <button type="button" className="cbf-btn cbf-btn-secondary cbf-btn-block" onClick={() => controller.back()} disabled={state.status === 'submitting'}>
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
